import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Brain, 
  FileText, 
  Zap, 
  CheckCircle, 
  Clock,
  Database,
  BarChart3,
  Building
} from 'lucide-react';
import { classNames } from '../../utils/classNames';

interface LoadingStage {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  duration: number; // in milliseconds
}

const AGENT_STAGES: LoadingStage[] = [
  {
    id: 'evidence',
    title: 'Evidence Gathering',
    description: 'Analyzing congressional data and finding relevant legislation...',
    icon: <Search className="w-5 h-5" />,
    duration: 8000
  },
  {
    id: 'tools_round_1',
    title: 'Tool Execution - Round 1',
    description: 'Running metrics analysis and trend identification tools...',
    icon: <Database className="w-5 h-5" />,
    duration: 12000
  },
  {
    id: 'tools_round_2',
    title: 'Tool Execution - Round 2',
    description: 'Generating charts, maps, and sponsor analysis...',
    icon: <BarChart3 className="w-5 h-5" />,
    duration: 10000
  },
  {
    id: 'tools_round_3',
    title: 'Tool Execution - Round 3',
    description: 'Analyzing committee involvement and geographic patterns...',
    icon: <Building className="w-5 h-5" />,
    duration: 8000
  },
  {
    id: 'planning',
    title: 'Report Planning',
    description: 'Structuring insights and organizing findings...',
    icon: <Brain className="w-5 h-5" />,
    duration: 15000
  },
  {
    id: 'writing',
    title: 'Writing Report',
    description: 'Generating narrative analysis and key insights...',
    icon: <FileText className="w-5 h-5" />,
    duration: 30000
  },
  {
    id: 'assembly',
    title: 'Final Assembly',
    description: 'Compiling dashboard with charts and tables...',
    icon: <Zap className="w-5 h-5" />,
    duration: 5000
  }
];

interface AgentLoadingSequenceProps {
  isVisible: boolean;
  onComplete?: () => void;
  onCancel?: () => void;
  isCanceling?: boolean;
  /** Optional override to scale the entire sequence to this total duration (ms) */
  totalDurationMs?: number;
}

export const AgentLoadingSequence: React.FC<AgentLoadingSequenceProps> = ({ 
  isVisible, 
  onComplete,
  onCancel,
  isCanceling: externalIsCanceling = false,
  totalDurationMs,
}) => {
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [completedStages, setCompletedStages] = useState<Set<string>>(new Set());
  const [stageProgress, setStageProgress] = useState(0);
  const [internalIsCanceling, setInternalIsCanceling] = useState(false);
  
  // Use external cancel state if provided, otherwise use internal state
  const isCanceling = externalIsCanceling || internalIsCanceling;

  // Optionally scale stages to fit a target total duration
  const stages = React.useMemo(() => {
    if (!totalDurationMs || totalDurationMs <= 0) return AGENT_STAGES;
    const totalOriginal = AGENT_STAGES.reduce((sum, s) => sum + s.duration, 0);
    if (totalOriginal <= 0) return AGENT_STAGES;
    const factor = totalDurationMs / totalOriginal;
    return AGENT_STAGES.map(s => ({
      ...s,
      // Ensure at least 300ms so animations are visible
      duration: Math.max(300, Math.round(s.duration * factor))
    }));
  }, [totalDurationMs]);

  useEffect(() => {
    if (!isVisible) {
      setCurrentStageIndex(0);
      setCompletedStages(new Set());
      setStageProgress(0);
      setInternalIsCanceling(false);
      return;
    }

    // Reset internal cancel state when component becomes visible (only if not using external state)
    if (!externalIsCanceling) {
      setInternalIsCanceling(false);
    }

    const currentStage = stages[currentStageIndex];
    if (!currentStage) {
      onComplete?.();
      return;
    }

    // Progress animation for current stage
    const progressInterval = setInterval(() => {
      setStageProgress(prev => {
        const increment = 100 / (currentStage.duration / 50);
        return Math.min(prev + increment, 100);
      });
    }, 50);

    // Stage completion timer
    const stageTimer = setTimeout(() => {
      setCompletedStages(prev => new Set(Array.from(prev).concat([currentStage.id])));
      setStageProgress(0);
      
      if (currentStageIndex < stages.length - 1) {
        setCurrentStageIndex(prev => prev + 1);
      } else {
        setTimeout(() => {
          if (!externalIsCanceling) {
            setInternalIsCanceling(false);
          }
          onComplete?.();
        }, 500);
      }
    }, currentStage.duration);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(stageTimer);
    };
  }, [currentStageIndex, isVisible, onComplete, stages, externalIsCanceling]);

  if (!isVisible) return null;

  const handleCancel = () => {
    if (isCanceling || !onCancel) return;
    if (!externalIsCanceling) {
      setInternalIsCanceling(true);
    }
    onCancel();
  };

  const currentStage = stages[currentStageIndex];
  const overallProgress = ((currentStageIndex + stageProgress / 100) / stages.length) * 100;

  return (
    <div className="fixed inset-0 theme-bg-primary/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl mx-auto h-full flex items-center">
        <div className="theme-bg-card backdrop-blur-sm rounded-3xl border-2 border-opacity-30 theme-border p-4 md:p-8 shadow-2xl shadow-black/20 w-full max-h-[85vh] md:max-h-[70vh] md:landscape:max-h-[75vh] lg:max-h-[75vh] overflow-y-auto">
          {/* Header */}
          <div className="text-center mb-6 md:mb-6 md:landscape:mb-5 lg:mb-8">
            <div className="w-12 h-12 md:w-14 md:landscape:w-13 md:h-14 md:landscape:h-13 lg:w-16 lg:h-16 bg-gradient-to-br from-[#e6ce8d] to-[#d6a650] rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-3 md:landscape:mb-3 lg:mb-4 animate-pulse">
              <Brain className="w-6 h-6 md:w-7 md:landscape:w-6.5 md:h-7 md:landscape:h-6.5 lg:w-8 lg:h-8 text-white" />
            </div>
            <h2 className="text-xl md:text-xl md:landscape:text-lg lg:text-2xl font-bold theme-text-primary mb-2">
              AI Agent Working
            </h2>
            <p className="theme-text-secondary text-sm md:text-sm md:landscape:text-sm lg:text-base">
              Analyzing legislative data and generating insights...
            </p>
          </div>

          {/* Overall Progress Bar */}
          <div className="mb-6 md:mb-6 md:landscape:mb-5 lg:mb-8">
            <div className="flex justify-between text-sm theme-text-secondary mb-2">
              <span>Overall Progress</span>
              <span>{Math.round(overallProgress)}%</span>
            </div>
            <div className="w-full theme-bg-tertiary/50 rounded-full h-2 md:h-2.5 md:landscape:h-2.5 lg:h-3 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#2c3f50] to-[#e6ce8d] transition-all duration-300 ease-out rounded-full"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>

          {/* Current Stage */}
          {currentStage && (
            <div className="mb-6 md:mb-6 md:landscape:mb-5 lg:mb-8">
              <div className="flex items-center space-x-2 md:space-x-3 mb-3 md:mb-3 md:landscape:mb-3 lg:mb-4">
                <div className="w-8 h-8 md:w-9 md:landscape:w-8 md:h-9 md:landscape:h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-[#2c3f50]/20 to-[#e6ce8d]/20 rounded-xl flex items-center justify-center border border-[#d6a650]/30 flex-shrink-0">
                  {currentStage.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base md:text-base md:landscape:text-sm lg:text-lg font-semibold theme-text-primary">
                    {currentStage.title}
                  </h3>
                  <p className="theme-text-secondary text-xs md:text-xs md:landscape:text-xs lg:text-sm">
                    {currentStage.description}
                  </p>
                </div>
              </div>
              
              {/* Stage Progress Bar */}
              <div className="w-full theme-bg-tertiary/50 rounded-full h-1.5 md:h-1.5 md:landscape:h-1.5 lg:h-2 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#d6a650] to-[#2c3f50] transition-all duration-100 ease-linear rounded-full"
                  style={{ width: `${stageProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Stage List */}
          <div className="space-y-1.5 md:space-y-1.5 md:landscape:space-y-2 lg:space-y-3">
            {stages.map((stage, index) => {
              const isCompleted = completedStages.has(stage.id);
              const isCurrent = index === currentStageIndex;

              return (
                <div 
                  key={stage.id}
                  className={classNames(
                    'flex items-center space-x-2 md:space-x-3 p-1.5 md:p-1.5 md:landscape:p-2 lg:p-3 rounded-lg md:rounded-xl transition-all duration-300',
                    isCompleted ? 'bg-[#d6a650]/10 border border-[#d6a650]/30' :
                    isCurrent ? 'loading-current-step' :
                    'theme-bg-tertiary/30 border theme-border/30'
                  )}
                >
                  <div className={classNames(
                    'w-5 h-5 md:w-7 md:landscape:w-6 md:h-7 md:landscape:h-6 lg:w-8 lg:h-8 rounded-lg flex items-center justify-center transition-all duration-300 flex-shrink-0',
                    isCompleted ? 'bg-[#d6a650]/20 text-[#d6a650]' :
                    isCurrent ? 'loading-current-step-icon' :
                    'theme-bg-tertiary/50 theme-text-muted'
                  )}>
                    {isCompleted ? (
                      <CheckCircle className="w-2.5 h-2.5 md:w-3.5 md:landscape:w-3 md:h-3.5 md:landscape:h-3 lg:w-4 lg:h-4" />
                    ) : isCurrent ? (
                      <Clock className="w-2.5 h-2.5 md:w-3.5 md:landscape:w-3 md:h-3.5 md:landscape:h-3 lg:w-4 lg:h-4 animate-spin" />
                    ) : (
                      <div className="w-2.5 h-2.5 md:w-3.5 md:landscape:w-3 md:h-3.5 md:landscape:h-3 lg:w-4 lg:h-4 flex items-center justify-center">
                        {stage.icon}
                      </div>
                    )}
                  </div>
                  
                  <span className={classNames(
                    'text-xs md:text-sm font-medium transition-all duration-300 flex-1 min-w-0 truncate',
                    isCompleted ? 'text-[#d6a650]' :
                    isCurrent ? 'loading-current-step-text' :
                    'theme-text-secondary'
                  )}>
                    {stage.title}
                  </span>
                  
                  {isCompleted && (
                    <div className="ml-auto flex-shrink-0">
                      <CheckCircle className="w-2.5 h-2.5 md:w-3.5 md:landscape:w-3 md:h-3.5 md:landscape:h-3 lg:w-4 lg:h-4 text-[#d6a650]" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-6 md:mt-4 md:landscape:mt-4 lg:mt-8 text-center space-y-3 md:space-y-2 md:landscape:space-y-3 lg:space-y-4">
            <p className="text-xs theme-text-muted">
              {totalDurationMs ? `This will take about ${Math.round(totalDurationMs/1000)} seconds in demo mode.` : 'This may take 1-2 minutes depending on query complexity'}
            </p>
            {onCancel && (
              <button
                onClick={handleCancel}
                disabled={isCanceling}
                className={classNames(
                  "px-3 py-2 md:px-3 md:landscape:px-3 md:py-2 md:landscape:py-2 lg:px-4 lg:py-2 rounded-lg font-medium transition-all duration-200 text-sm w-full lg:w-auto",
                  isCanceling 
                    ? "theme-bg-tertiary/50 theme-text-muted border-2 border-gray-400/30 cursor-not-allowed" 
                    : "theme-bg-tertiary hover:theme-bg-secondary theme-text-secondary hover:theme-text-primary border-2 border-red-500/30 hover:border-red-500/50"
                )}
              >
                {isCanceling ? (
                  <span className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    <span>Canceling...</span>
                  </span>
                ) : (
                  "Cancel Analysis"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};